import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { PencilSquareIcon as PencilOutlineIcon } from "@heroicons/react/24/outline";
import { PencilSquareIcon as PencilSolidIcon } from "@heroicons/react/24/solid";

// Inline per-row note editor for the customer-sync review grid, mirroring the bulk-meter RowNoteCell.
// Unlike bulk meter (whose note is a deferred declaration note), this persists immediately via the
// PATCH .../rows/:id/note mutation the parent wires to onSave. Only matched rows (in_sync /
// has_differences) can carry a note; the parent gates rendering on matchedSubscriberId + CreateNotes.
type RowNoteCellProps = {
  note: string;
  saving: boolean;
  onSave: (note: string) => void;
};

const RowNoteCell: React.FunctionComponent<RowNoteCellProps> = ({ note, saving, onSave }) => {
  const { t } = useTranslation();
  const hasNote = note.trim() !== "";
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState(note);

  const addLabel = t("subscriber_sync_upload.modal.note_label");
  const editLabel = t("common.edit");

  const openEditor = () => {
    setValue(note);
    setOpen(true);
  };

  const commit = (next: string) => {
    onSave(next.trim());
    setOpen(false);
  };

  return (
    <div className="relative flex items-center gap-2">
      {hasNote ? (
        <span
          className="block max-w-[12rem] truncate text-xs text-gray-700"
          title={note}
          data-testid="row-note-text"
        >
          {note}
        </span>
      ) : null}
      <button
        type="button"
        data-row-note
        data-has-note={hasNote ? "true" : "false"}
        aria-label={hasNote ? editLabel : addLabel}
        title={hasNote ? editLabel : addLabel}
        onClick={openEditor}
        data-testid="row-note-trigger"
        className={classNames(
          "inline-flex items-center rounded px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500",
          hasNote
            ? "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            : "text-gray-400 hover:bg-gray-100 hover:text-gray-700",
        )}
      >
        {hasNote ? (
          <PencilSolidIcon className="h-4 w-4" aria-hidden="true" />
        ) : (
          <PencilOutlineIcon className="h-4 w-4" aria-hidden="true" />
        )}
      </button>

      {open ? (
        <div className="absolute left-0 top-6 z-10 mt-1 w-64 rounded border border-gray-200 bg-white p-3 shadow-lg">
          <textarea
            autoFocus
            rows={3}
            value={value}
            data-testid="row-note-editor"
            aria-label={addLabel || undefined}
            placeholder={t("subscriber_sync_upload.modal.note_placeholder") || undefined}
            onChange={e => setValue(e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-blue-500"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              className="btn-outline-primary text-xs rounded"
              onClick={() => setOpen(false)}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="btn-primary text-xs rounded"
              disabled={saving}
              onClick={() => commit(value)}
            >
              {t("common.save")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RowNoteCell;
